import { difference, get, head, map } from 'lodash';
import { Field, FieldArray, InjectedFormProps } from 'redux-form';
import {
  IDestination,
  IPolicyApplication,
  IPremium,
  IProduct,
  IStateCities,
  TripType
} from '../../../../../../data/api/models';
import { II18nMessages } from '../../../../../../services/branding/models';
import { IQuoteActions } from '../../../../../../services/quote/models';
import { REGION_WORLDWIDE_EXCLUDING_LABEL_OLD } from '../../../../index';
import { showDestinations, showRegions } from '../../../../../../data/api/api';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import SelectControl from '../../../../../../components/SelectControl';
import {
  annualLimit,
  validateIsFutureDate,
  isRequired,
  isValidDate,
  minTripDuration,
  singleLimit,
  singleMaxTripDuration
} from '../../../../../../util/validators';
import * as Commonmark from 'react-commonmark';
import FieldFormControl from '../../../../../../components/FieldFormControl';
import { addDays, parse } from 'date-fns';
import AlternativeDatePicker from '../../../../../../components/AlternativeDatePicker';
import { FaInfoCircle } from 'react-icons/fa/';
import Destination from './Components/Destination';
import StateCities from './Components/StateCities';
import { FormHelper } from '../../../../../../helpers/form-helper';
import React = require('react');
import { useEffect, useRef } from 'react';
import { RemoveUkRegion } from '../../../../../../helpers/RemoveUkRegion';

interface ISelectCoverDatesAndDestinationsProps extends InjectedFormProps {
  destinations: IDestination[];
  stateCities: IStateCities[];
  i18n: II18nMessages;
  tripType: TripType;
  startDate: string;
  region: string;
  endDate: string;
  dispatch: any;
  product: IProduct;
  destinationsValue: string[];
  stateCitiesValue: string[];
  quote: IPolicyApplication;
  premiums?: IPremium[];
  quoteForEdit?: boolean;

  renderDefinition(name: string, useProvidedString?: boolean): any;

  actions: {
    quote: IQuoteActions;
  };
  createdBeforeFixedSchemeVersionRule?: boolean;
}

export const SelectCoverDatesAndDestinations = ({
                                                  tripType,
                                                  region,
                                                  destinationsValue,
                                                  product,
                                                  createdBeforeFixedSchemeVersionRule,
                                                  i18n,
                                                  destinations,
                                                  stateCities,
                                                  stateCitiesValue,
                                                  renderDefinition,
                                                  startDate,
                                                  actions,
                                                  ...props
                                                }: ISelectCoverDatesAndDestinationsProps) => {

  const startDatePicker = React.createRef();

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }
  const hideUKRegion = get(head(props?.premiums), 'information.hideUkRegion', false)
  const productRegions = RemoveUkRegion(product.metadata.regions, props?.quoteForEdit, hideUKRegion)
  const hasUKRegion = get(productRegions, 'uk', null) !== null;

  const regionsList = map(productRegions, (name, key) => {
    if (key === 'worldwide_excluding' && createdBeforeFixedSchemeVersionRule) {
      return {
        label: REGION_WORLDWIDE_EXCLUDING_LABEL_OLD,
        value: key,
      };
    }

    return {
      label: name,
      value: key,
    };
  });

  const previousValues = usePrevious({ destinationsValue, region });

  useEffect(() => {
    const previousDestinations = get(previousValues, 'destinationsValue');
    const previousRegion = get(previousValues, 'region');

    if (destinationsValue && difference(previousDestinations, destinationsValue).length > 0) {
      const hasEmpty = destinationsValue.includes('') || destinationsValue.includes(undefined)
      if (!hasEmpty) {
        actions.quote.recalculateScreening()
      }
    }

    if (region && previousRegion !== undefined && previousRegion !== region) {
      actions.quote.recalculateScreening()
    }
  }, [region, destinationsValue])

  const onClickEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  }

  const regionsInfoContent = (
    <Popover>
      <div>
        <div><Commonmark source={product.metadata.definitions.region?.replace(/\*\*.*?\*\*/, '')}/></div>
        <div className={'scd--popover-title'}>Regions</div>
      </div>
      {hasUKRegion && <Commonmark source={product.metadata.definitions.region_uk}/>}
      <Commonmark source={product.metadata.definitions.region_europe}/>
      <Commonmark source={
        createdBeforeFixedSchemeVersionRule
          ? product.metadata.definitions.region_worldwide_excluding_old
          : product.metadata.definitions.region_worldwide_excluding
      }
      />
      <Commonmark source={
        createdBeforeFixedSchemeVersionRule
          ? product.metadata.definitions.region_worldwide_old
          : product.metadata.definitions.region_worldwide
      }
      />
    </Popover>
  );

  const regionsInfo = (
    <OverlayTrigger
      trigger="click"
      rootClose={true}
      placement="right"
      overlay={regionsInfoContent}
    >
      <FaInfoCircle className="info-btn"/>
    </OverlayTrigger>
  );

  const renderDepartureDate = () => {
    return <div>
      <div className="date-container">
        <div className={'flex--50 pt-10 pb-10'}>
          {showDestinations(tripType) ? 'Departure date ' : 'Cover Start Date '}
          {showDestinations(tripType) ?
            renderDefinition('travel_dates') :
            renderDefinition('travel_dates_annual')}
        </div>
        <div className="date-picker flex--stretch" onClick={onClickEvent}>
          <div className="alternative-date-picker">
            <div className="field start-date-input">
              <Field
                name="startDate"
                type="masked-date"
                inputmode="numeric"
                placeholder="dd/mm/yyyy"
                component={FieldFormControl}
                validate={[isRequired, isValidDate]}
              />
            </div>
            <div className="icon" ref={startDatePicker}>
              <Field
                name="startDate"
                minDate={new Date()}
                maxDate={showRegions(tripType) ?
                  addDays(new Date(), 180) :
                  addDays(new Date(), 365)
                }
                component={AlternativeDatePicker}
                validate={showRegions(tripType) ? [
                  isRequired,
                  validateIsFutureDate,
                  isValidDate,
                  annualLimit,
                ] : [
                  isRequired,
                  validateIsFutureDate,
                  isValidDate,
                  singleLimit,
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>;
  }

  const renderImportantMessage = () => {
    if (!isValidDate(startDate)) {
      return tripType === 'annual' && (
        <div className={'padding-0'}>
          <div className="important-inline-section yellow" style={{ marginBottom: '20px' }}>
            <div className="important yellow" style={{ padding: 0 }}>
              <strong className={'yellow'}>IMPORTANT</strong> - {i18n.quote.tripDetails.coverWarning}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const renderReturnDate = () => {
    return tripType === 'single' ? (
      <div>
        <div className="date-container">
          <div className={'flex--50 pt-10 pb-10'} onClick={onClickEvent}>
            Return date {renderDefinition('travel_dates')}
          </div>
          <div className="date-picker flex--stretch" onClick={onClickEvent}>
            <div className="alternative-date-picker">
              <div className="field end-date-input">
                <Field
                  name="endDate"
                  type="masked-date"
                  inputmode="numeric"
                  placeholder="dd/mm/yyyy"
                  component={FieldFormControl}
                />
              </div>
              <div className="icon">
                <Field
                  name="endDate"
                  minDate={startDate ? addDays(parse(startDate, 'dd/MM/yyyy', new Date()), 1) : new Date()}
                  maxDate={
                    parse(startDate, 'dd/MM/yyyy', new Date()) < addDays(new Date(), 91) ?
                      addDays(new Date(), 365) :
                      addDays(new Date(), 455)
                  }
                  component={AlternativeDatePicker}
                  validate={[
                    isRequired,
                    validateIsFutureDate,
                    isValidDate,
                    minTripDuration(startDate),
                    singleMaxTripDuration(startDate),
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
      FormHelper.disabledValidationField('endDate')
    );
  }

  const renderDestinations = () => {
    return showDestinations(tripType) ? (
      <div>
        <FieldArray
          name="destinations"
          i18n={i18n}
          destinations={destinations}
          component={Destination}
          destinationsValue={destinationsValue}
          renderDefinition={renderDefinition}
        />
        <FieldArray
          name="stateCities"
          i18n={i18n}
          stateCities={stateCities}
          component={StateCities}
          stateCitiesValue={stateCitiesValue}
          destinationsValue={destinationsValue}
          renderDefinition={renderDefinition}
        />
      </div>
    ) : (
      // Destination component internally sets this name for the field: destinations[0]
      <>
        {FormHelper.disabledValidationField('destinations[0]')}
        {FormHelper.disabledValidationField('stateCities[0]')}
      </>
    );
  }

  const renderRegions = () => {
    return showRegions(tripType) ? (<>
      <div className="date-container">
        <div className={'flex--50 flex--stretch pt-10 pb-10'} style={{ display: 'flex' }}>
          {i18n.quote.tripDetails.destinations}{' '}
          {regionsInfo}
        </div>
        <div className={'flex--50 flex--stretch'}>
          <Field
            name="region"
            id="destinations-inputs"
            type="text"
            component={SelectControl}
            forceFilter={false}
            options={regionsList}
            validate={[
              isRequired,
            ]}
          />
        </div>
      </div>
      {/*<div className="info-note">*/}
      {/*  {i18n.quote.tripDetails.europeanIncluded}{' '}*/}
      {/*</div>*/}
    </>) : (
      FormHelper.disabledValidationField('region')
    );
  }

  return (
    <>
      {/* SINGLE */}
      {renderDestinations()}
      {/* Annual|LongStay / Regions */}
      {renderRegions()}
      {/* ALWAYS */}
      {renderDepartureDate()}
      {/* ANNUAL */}
      {renderImportantMessage()}
      {/* SINGLE */}
      {renderReturnDate()}
    </>
  )
}
